import { useEffect } from 'react';
import { getWindow } from '../../../../lib/getWindow';
import { isIOS } from '../../../../lib/isIOS';
import { VIRTUAL_KEYBOARD_OFFSET_VAR } from '../constants';
import { type DrawerRefs } from '../types';

const POLLING_DELAY = 50;
const UPDATE_DELAY = 500;

const OFFSET_MARGIN = 50;

/*
 * Корректирует позицию drawer при открытии виртуальной клавиатуры на iOS.
 *
 * При изменении visualViewport (клавиатура, меню автозаполнения) drawer
 * автоматически сдвигается вверх, оставаясь прижатым к низу видимой области.
 *
 * Использует комбинацию событий resize и polling, т.к. Safari
 * может пропускать события resize для visualViewport/window при открытии/закрытии клавиатуры.
 *
 * Работает только на iOS устройствах.
 */
export const useVirtualKeyboardOffset = (isEnabled: boolean, drawerRef: DrawerRefs['drawerRef']) => {
    useEffect(() => {
        const drawer = drawerRef.current;
        const window = getWindow();

        if (!isEnabled || !window || !isIOS()) {
            return;
        }

        const visualViewport = window.visualViewport;

        if (!visualViewport || !drawerRef.current) {
            return;
        }

        let previousWindowHeight = window.innerHeight;
        let previousVisualHeight = visualViewport.height;
        let updateTimeout: NodeJS.Timeout | null = null;

        /*
         * Применяет offset к drawer с проверкой актуальности
         */
        const applyOffset = () => {
            if (!drawer) {
                return;
            }

            // Проверяем актуальные значения viewport в момент применения
            const currentWindowHeight = window.innerHeight;
            const currentVisualHeight = visualViewport.height;
            const currentKeyboardHeight = currentWindowHeight - currentVisualHeight;

            // Применяем коррекцию только если клавиатура действительно открыта (> OFFSET_MARGIN)
            const offset = currentKeyboardHeight > OFFSET_MARGIN ? `-${currentKeyboardHeight}px` : '0px';

            // Применяем с плавной transition
            drawer.style.setProperty(VIRTUAL_KEYBOARD_OFFSET_VAR, offset);
        };

        /*
         * Обработчик изменения viewport
         */
        const handleViewportResize = (source: 'event' | 'polling' = 'event') => {
            if (!drawer) {
                return;
            }

            const windowHeight = window.innerHeight;
            const visualHeight = visualViewport.height;

            const windowHeightChanged = windowHeight !== previousWindowHeight;
            const visualHeightChanged = visualHeight !== previousVisualHeight;

            // При polling - пропускаем если ничего не изменилось
            if (source === 'polling' && !windowHeightChanged && !visualHeightChanged) {
                return;
            }

            previousWindowHeight = windowHeight;
            previousVisualHeight = visualHeight;

            if (updateTimeout) {
                clearTimeout(updateTimeout);
            }

            updateTimeout = setTimeout(() => {
                applyOffset();
            }, UPDATE_DELAY);
        };

        const handleVisualViewportResize = () => handleViewportResize('event');
        const handleWindowResize = () => handleViewportResize('event');

        visualViewport.addEventListener('resize', handleVisualViewportResize);
        window.addEventListener('resize', handleWindowResize);

        // Polling для отлова пропущенных событий Safari
        const pollingInterval = setInterval(() => {
            handleViewportResize('polling');
        }, POLLING_DELAY);

        // Устанавливаем начальную позицию
        handleViewportResize('event');

        return () => {
            visualViewport.removeEventListener('resize', handleVisualViewportResize);
            window.removeEventListener('resize', handleWindowResize);
            clearInterval(pollingInterval);

            if (updateTimeout) {
                clearTimeout(updateTimeout);
            }

            if (drawer) {
                drawer.style.setProperty(VIRTUAL_KEYBOARD_OFFSET_VAR, '0px');
            }
        };
    }, [isEnabled, drawerRef]);
};
